import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const score = (n, words) => {
  const label = norm(n.label);
  const hay = `${label} ${norm(n.parent)} ${norm(n.kw)}`;
  const hit = (h, w) => h.includes(w) || (w.length > 3 && w.endsWith("s") && h.includes(w.slice(0, -1)));
  if (!words.every((w) => hit(hay, w))) return 0;
  return 1 + words.filter((w) => hit(label, w)).length * 2 + (label.startsWith(words[0]) ? 3 : 0);
};

function Results({ found, sel, setSel, go }) {
  if (!found.length) return <p className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="nav-search-empty">Nada encontrado</p>;
  return (
    <div className="flex flex-col gap-1" data-testid="nav-search-results">
      {found.map(({ to, label, icon: Icon, parent }, i) => (
        <button key={`${to}${label}`} onMouseEnter={() => setSel(i)} onClick={() => go(found[i])} data-testid={`nav-search-result-${i}`}
          className={cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors", i === sel ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent")}>
          <Icon className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1"><span className="block truncate">{label}</span>{parent && <span className="block truncate text-[11px] font-normal text-muted-foreground">{parent}</span>}</span>
        </button>
      ))}
    </div>
  );
}

export function NavSearch({ items, onNavigate, hotkey, children }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();
  const found = useMemo(() => {
    const words = norm(q).trim().split(/\s+/).filter(Boolean);
    return items.map((n) => [n, score(n, words)]).filter(([, s]) => s).sort((a, b) => b[1] - a[1]).slice(0, 15).map(([n]) => n);
  }, [q, items]);
  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    if (!hotkey) return undefined;
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [hotkey]);
  const go = (n) => { if (!n) return; navigate(n.to); setQ(""); ref.current?.blur(); onNavigate?.(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, found.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") go(found[sel]);
    else if (e.key === "Escape") setQ("");
  };
  return (
    <>
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Buscar no sistema"
          className="h-10 w-full rounded-lg border bg-background pl-9 pr-14 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20" data-testid="nav-search-input" />
        {q ? <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" data-testid="nav-search-clear"><X className="h-4 w-4" /></button>
          : hotkey && <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border px-1.5 font-mono text-[10px] text-muted-foreground">Ctrl K</kbd>}
      </div>
      {q.trim() ? <Results found={found} sel={sel} setSel={setSel} go={go} /> : children}
    </>
  );
}
